import React, { useEffect, useRef } from 'react'
import Swal from 'sweetalert2'
import IPModal from '../../../../components/IPModal'
import IPInput from '../../../../components/IPInput'
import IPButton from '../../../../components/IPButton'
import { useCustomReducer } from '../../../../base/customHooks'
import { CardPhoneApi, CardPhoneStatus } from '../services/cardPhoneApi'

interface ICardPhoneVerificationModalProps {
  show: boolean
  paymentControlId: number
  shortText: string | null
  onVerified: () => void
  onClose: () => void
}

interface IState {
  step: number
  phone: string
  code: string
  verificationId: number
  maskedPhone: string
  waitingConsent: boolean
  busy: boolean
  // Sayaçlar saniye cinsinden tutulur; sunucudan da saniye gelir, her durum
  // yoklamasında yeniden senkronlanır (istemci saati sapsa bile doğru kalır).
  resendInSeconds: number
  expireInSeconds: number
  codeExpireInSeconds: number
}

const initialState: IState = {
  step: 1,
  phone: '',
  code: '',
  verificationId: 0,
  maskedPhone: '',
  waitingConsent: false,
  busy: false,
  resendInSeconds: 0,
  expireInSeconds: 0,
  codeExpireInSeconds: 0,
}

/** 95 -> "01:35" */
const formatCountdown = (totalSeconds: number) => {
  const s = Math.max(0, totalSeconds)
  const mm = Math.floor(s / 60)
  const ss = s % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

/**
 * Ödeme öncesi kart sahibinden SMS ile onay alan iki adımlı pop-up.
 * Adım 1: bayi son kullanıcının telefonunu girer, müşteriye onay linki gider.
 * Adım 2: müşteri linkte onaylayınca kod SMS'i gider, bayi kodu girer.
 * Öde butonunu açan gerçek kapı sunucu tarafındadır; bu pop-up yalnız akışı yürütür.
 */
function CardPhoneVerificationModal(props: ICardPhoneVerificationModalProps) {
  const [state, setState] = useCustomReducer<IState>(initialState)
  const pollRef = useRef<any>(null)

  // useCustomReducer fonksiyonel güncelleme desteklemediği için (yalnız Partial<T>),
  // sayaç interval'i güncel değerleri bu ref'ten okur. Böylece interval saniyede bir
  // yeniden kurulmaz; adım başına bir kez kurulup düzenli aralıkla çalışır.
  const latest = useRef(state)
  useEffect(() => {
    latest.current = state
  }, [state])

  // Saniyelik sayaç. Yalnızca 2. adımda çalışır; pop-up kapanınca durur.
  useEffect(() => {
    if (!props.show || state.step !== 2) return

    const id = setInterval(() => {
      const c = latest.current
      setState({
        resendInSeconds: Math.max(0, c.resendInSeconds - 1),
        expireInSeconds: Math.max(0, c.expireInSeconds - 1),
        codeExpireInSeconds: Math.max(0, c.codeExpireInSeconds - 1),
      })
    }, 1000)

    return () => clearInterval(id)
  }, [props.show, state.step])

  // Müşteri onayı gelene kadar 3 sn'de bir durum yoklanır. Sayaçlar da burada
  // sunucu değerleriyle yeniden senkronlanır.
  useEffect(() => {
    if (!state.waitingConsent || state.verificationId === 0) return

    pollRef.current = setInterval(async () => {
      try {
        const res = await new CardPhoneApi({}).getStatus(state.verificationId)
        if (!res) return

        setState({
          expireInSeconds: res.ExpireInSeconds,
          codeExpireInSeconds: res.CodeExpireInSeconds,
          resendInSeconds: res.ResendInSeconds,
        })

        if (res.Status === CardPhoneStatus.Consented) {
          setState({ waitingConsent: false })
        } else if (res.Status === CardPhoneStatus.Expired || res.Status === CardPhoneStatus.Cancelled) {
          setState({ waitingConsent: false, step: 1, verificationId: 0 })
          Swal.fire('Doğrulama süresi doldu', 'Lütfen yeniden onay alınız.', 'warning')
        }
      } catch (err) {
        console.error(err)
      }
    }, 3000)

    return () => clearInterval(pollRef.current)
  }, [state.waitingConsent, state.verificationId])

  const sendLink = async () => {
    setState({ busy: true })
    try {
      const res = await new CardPhoneApi({}).sendLink(props.paymentControlId, state.phone)
      if (res) {
        setState({
          step: 2,
          verificationId: res.VerificationId,
          maskedPhone: res.MaskedPhone,
          resendInSeconds: res.ResendInSeconds,
          expireInSeconds: res.ExpireInSeconds,
          codeExpireInSeconds: 0,
          waitingConsent: true,
        })
      }
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Bağlantı gönderilemedi.', 'error')
    } finally {
      setState({ busy: false })
    }
  }

  const resendLink = async () => {
    if (state.resendInSeconds > 0) return
    setState({ busy: true })
    try {
      const res = await new CardPhoneApi({}).resendLink(state.verificationId)
      if (res) {
        setState({
          resendInSeconds: res.ResendInSeconds,
          expireInSeconds: res.ExpireInSeconds,
          waitingConsent: true,
        })
      }
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Bağlantı gönderilemedi.', 'error')
    } finally {
      setState({ busy: false })
    }
  }

  const verifyCode = async () => {
    setState({ busy: true })
    try {
      const res = await new CardPhoneApi({}).verifyCode(state.verificationId, state.code)
      if (res) {
        setState(initialState)
        props.onVerified()
      }
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Kod doğrulanamadı.', 'error')
    } finally {
      setState({ busy: false })
    }
  }

  const codeExpired = !state.waitingConsent && state.codeExpireInSeconds <= 0

  return (
    <IPModal show={props.show} onClose={props.onClose} hideConfirm={true} width={520} title='Ödeme Onayı'>
      {state.step === 1 ? (
        <div className='form-group col-md-12'>
          {/* Kısa yazı sunucudaki şablon dosyasından geliyor (repo içeriği, kullanıcı girdisi değil). */}
          <div className='mb-3' dangerouslySetInnerHTML={{ __html: props.shortText || '' }} />
          <label className='text-sm-left'>Müşterinin Cep Telefonu Numarası:</label>
          <IPInput
            type='text'
            className='form-control'
            name='cardPhone'
            required
            placeholder='5XX XXX XX XX'
            value={state.phone}
            onChange={e => setState({ phone: e.target.value })}
          />
          <div className='mt-3 text-end'>
            <IPButton text='Onay Bağlantısı Gönder' type='button' disabled={state.busy} onClick={sendLink} />
          </div>
        </div>
      ) : (
        <div className='form-group col-md-12'>
          <div className='mb-3'>{state.maskedPhone} numaralı telefona onay bağlantısı gönderildi.</div>

          {state.waitingConsent ? (
            <div className='mb-3'>
              <div>Müşteri onayı bekleniyor…</div>
              <div className='mt-1'>
                Bağlantının geçerlilik süresi: <strong>{formatCountdown(state.expireInSeconds)}</strong>
              </div>
              {state.expireInSeconds === 0 && (
                <div className='text-danger mt-1'>
                  Bağlantının süresi doldu. Yeniden gönderiniz.
                </div>
              )}
            </div>
          ) : (
            <>
              <div className='mb-2'>Müşteri onayı alındı. Müşterinin ekranında görünen 6 haneli kodu giriniz.</div>
              <div className='mb-3'>
                {codeExpired ? (
                  <span className='text-danger'>Kodun süresi doldu. Bağlantıyı yeniden gönderiniz.</span>
                ) : (
                  <>
                    Kodun geçerlilik süresi: <strong>{formatCountdown(state.codeExpireInSeconds)}</strong>
                  </>
                )}
              </div>
              <label className='text-sm-left'>Doğrulama Kodu</label>
              <IPInput
                type='text'
                className='form-control'
                name='cardPhoneCode'
                required
                maxLength={6}
                placeholder='6 haneli kod'
                value={state.code}
                onChange={e => setState({ code: e.target.value })}
              />
              <div className='mt-3 text-end'>
                <IPButton
                  text='Doğrula'
                  type='button'
                  disabled={state.busy || codeExpired}
                  onClick={verifyCode}
                />
              </div>
            </>
          )}

          <div className='mt-3'>
            <IPButton
              text={
                state.resendInSeconds > 0
                  ? `Bağlantıyı Yeniden Gönder (${formatCountdown(state.resendInSeconds)})`
                  : 'Bağlantıyı Yeniden Gönder'
              }
              type='button'
              disabled={state.busy || state.resendInSeconds > 0}
              onClick={resendLink}
            />
          </div>
        </div>
      )}
    </IPModal>
  )
}

export default CardPhoneVerificationModal
